import { Candidate, Capitals, Capabilities, isUlid, parseCandidate, parseCapabilities, parseSelection, Selection } from "./types";

export class CandidateApiError extends Error {
  constructor(public readonly code: string, public readonly status: number) { super(code); }
}

/** This client preserves diagnostic projections returned with 409; the legacy HTTP helper discards them. */
export default class PricingCandidatesRepository {
  constructor(
    private readonly leadId: string,
    private readonly token: string,
    private readonly base = process.env.REACT_APP_CUSTOMER_APP_URL,
    private readonly request: typeof fetch = (...args) => fetch(...args),
  ) {}

  private async call(path: string, body?: object): Promise<{ status: number; value: unknown }> {
    if (!isUlid(this.leadId) || !this.token || !this.base) throw new CandidateApiError("CANDIDATE_ACCESS_UNAVAILABLE", 403);
    const response = await this.request(`${this.base.replace(/\/$/, "")}/api/leads/${this.leadId}/${path}`, {
      method: body ? "PUT" : "GET", cache: "no-store", redirect: "error",
      headers: { Authorization: `Bearer ${this.token}`, Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const value = await response.json();
    if ([200, 202].includes(response.status) || (response.status === 409 && value?.schemaVersion)) return { status: response.status, value };
    const code = value?.diagnosticCode ?? value?.code ?? value?.errorCode ?? value?.error;
    throw new CandidateApiError(typeof code === "string" && /^[A-Z][A-Z0-9_]{0,79}$/.test(code) ? code : "CANDIDATE_REQUEST_FAILED", response.status);
  }

  async capabilities(): Promise<Capabilities> {
    const { status, value } = await this.call("pricing-capabilities");
    if (status !== 200) throw new CandidateApiError("INVALID_CAPABILITIES_STATUS", status);
    return parseCapabilities(value);
  }

  private checkOutcome(status: number, result: Candidate | Selection): void {
    const confirmed = result.state === "archived" || result.state === "selected";
    if (status === 200 && confirmed && result.nextAction === "none" && result.diagnosticCode === null) return;
    if (status === 202 && !confirmed && result.state !== "rate_unknown" && result.diagnosticCode !== "REMOTE_CONFLICT"
      && ["poll", "retry_same_intention"].includes(result.nextAction)) return;
    if (status === 409 && result.state !== "selected" && result.nextAction === "contact_support" && result.diagnosticCode !== null) return;
    throw new CandidateApiError("INVALID_CANDIDATE_OUTCOME_STATUS", status);
  }

  async candidate(id: string, options?: Capitals): Promise<Candidate> {
    if (!isUlid(id)) throw new CandidateApiError("INVALID_CANDIDATE_IDENTITY", 400);
    const { status, value } = await this.call(`pricing-candidates/${id}`, options ? { capitalsByTrait: options } : undefined);
    const result = parseCandidate(value);
    this.checkOutcome(status, result);
    if (result.candidateId !== id) throw new CandidateApiError("CANDIDATE_BINDING_CONFLICT", 409);
    return result;
  }

  async selection(id: string, candidateId?: string): Promise<Selection> {
    if (!isUlid(id) || (candidateId !== undefined && !isUlid(candidateId))) throw new CandidateApiError("INVALID_CANDIDATE_IDENTITY", 400);
    const { status, value } = await this.call(`pricing-selections/${id}`, candidateId ? { candidateId } : undefined);
    const result = parseSelection(value);
    this.checkOutcome(status, result);
    if (result.selectionId !== id || (candidateId && result.candidateId !== candidateId)) throw new CandidateApiError("CANDIDATE_SELECTION_CONFLICT", 409);
    return result;
  }
}
